/**
 * WallboardPage.jsx
 * Raumanzeige: die vier Eingangs-Warteschlangen nebeneinander auf einem
 * grossen Bildschirm.
 *
 * Bewusst anders gebaut als die uebrigen Ansichten:
 *   - Kein Zeitraum-Filter, keine Klickziele, keine Drilldowns. Niemand steht
 *     vor der Wand und bedient etwas — die Anzeige muss aus fuenf Metern
 *     Entfernung in zwei Sekunden lesbar sein.
 *   - Laedt sich selbst alle 5 Minuten neu. Der Antwort-Cache im Backend haelt
 *     4 Minuten, damit mehrere offene Tabs nicht mehr Last erzeugen als einer.
 *   - Kachel-Reihenfolge liegt fest, damit man nicht jedes Mal sucht.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useApi } from './useApi';
import './WallboardPage.css';

// Die Anzeige laedt sich selbst neu — am Wandbildschirm sitzt niemand, der
// F5 drueckt. Fuenf Minuten (Fachentscheid 18.09.2026); der Antwort-Cache im
// Backend liegt mit vier Minuten knapp darunter, damit mehrere offene Tabs
// nicht mehr Last erzeugen als einer.
const NEULADEN_MS = Number(process.env.REACT_APP_WALLBOARD_INTERVAL_MS) || 300_000;

// ─── Servicezeiten ───────────────────────────────────────────────────────────
//
// Ausserhalb der Servicezeiten fragt die Anzeige NICHTS mehr ab (Fachentscheid
// 18.09.2026): Der Bildschirm laeuft dann zwar weiter, aber niemand schaut
// hin — jede Abfrage waere verschenkte Zendesk- und CPU-Last auf einem ohnehin
// gedrosselten Container.
//
// Bewusst etwas weiter gefasst als die Geschaeftszeit-Rechnung (07:00-12:00 /
// 13:00-17:30): ueber Mittag wird durchgezaehlt, weil Tickets auch dann
// eintreffen und jemand hinschauen koennte, und abends laeuft es bis 18:00
// nach.
const SERVICE_VON = Number(process.env.REACT_APP_WALLBOARD_VON || 7);
const SERVICE_BIS = Number(process.env.REACT_APP_WALLBOARD_BIS || 18);

function istServicezeit(d = new Date()) {
  const tag = d.getDay();
  if (tag === 0 || tag === 6) return false;          // Sa/So
  const stunde = d.getHours() + d.getMinutes() / 60;
  return stunde >= SERVICE_VON && stunde < SERVICE_BIS;
}

function tagesSchluessel(d = new Date()) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Schwellen fuer die Ampel der Wartezeit, in Geschaeftsminuten
// (Fachentscheid 18.09.2026): bis 30 Min gruen, 31 bis 60 gelb, ab 61 rot.
// Bewusst hier und nicht im CSS: Es ist eine fachliche Festlegung, keine
// Gestaltungsfrage — und sie gehoert an EINE Stelle, damit sie diskutierbar
// bleibt.
const AMPEL_WARN     = 31;
const AMPEL_KRITISCH = 61;

/**
 * Liefert Ref und Stil, damit die Anzeige exakt bis zum unteren Fensterrand
 * reicht — kein Pixel mehr (Scrollbalken) und keiner weniger (verschenkte
 * Flaeche).
 *
 * Gemessen statt gerechnet: Unter /wallboard sitzt die Anzeige ganz oben,
 * im Reiter darunter die Kopfleiste. Deren Hoehe hier zu schaetzen hiesse,
 * dass jede Aenderung an der Kopfleiste das Wallboard still kaputt macht.
 */
function useVolleHoehe() {
  const ref = useRef(null);
  const [hoehe, setHoehe] = useState('100dvh');

  const messen = useCallback(() => {
    if (!ref.current) return;
    const oben = ref.current.getBoundingClientRect().top + window.scrollY;
    setHoehe(`calc(100dvh - ${Math.round(oben)}px)`);
  }, []);

  useEffect(() => {
    messen();
    window.addEventListener('resize', messen);
    // Der Browser gibt <body> standardmaessig 8px Aussenabstand, und das
    // Projekt setzt ihn nirgends zurueck. Der untere davon liegt UNTER der
    // exakt fensterhohen Anzeige und erzeugt genau dort den Scrollbalken.
    // Statt den Abstand global zu entfernen (das wuerde alle anderen Reiter
    // verschieben) wird das Scrollen nur abgeschaltet, solange das Wallboard
    // sichtbar ist. Verloren geht dabei nichts — der Bereich ist leer.
    document.body.classList.add('wallboard-aktiv');
    return () => {
      window.removeEventListener('resize', messen);
      document.body.classList.remove('wallboard-aktiv');
    };
  }, [messen]);

  return [ref, { height: hoehe }];
}

function ampelKlasse(minuten) {
  if (minuten == null) return '';
  if (minuten >= AMPEL_KRITISCH) return 'ist-kritisch';
  if (minuten >= AMPEL_WARN)     return 'ist-warn';
  return 'ist-ok';
}

// Ein Arbeitstag in Geschaeftsminuten: 07:00-12:00 plus 13:00-17:30 = 9h30.
// Muss zu BLOCKS in services/businessHoursService.js passen.
const ARBEITSTAG_MIN = 570;

/**
 * Formatiert GESCHAEFTSMINUTEN: 20 → „20 Min", 145 → „2 Std 25",
 * 1562 → „2,7 Arbeitstage".
 *
 * Oberhalb eines Arbeitstags bewusst in Arbeitstagen statt in Kalendertagen.
 * Die frühere Darstellung schrieb für 1562 Minuten „1 Tg 2 Std" — das liest
 * sich wie gut ein Tag, sind aber fast drei Arbeitstage, weil Nächte und
 * Wochenenden gar nicht mitzählen. Auf einer Wand wird so eine Zahl nicht
 * nachgerechnet, sie wird geglaubt.
 */
function dauer(minuten) {
  if (minuten == null) return '–';
  if (minuten < 60) return `${minuten} Min`;
  if (minuten < ARBEITSTAG_MIN) {
    return `${Math.floor(minuten / 60)} Std ${String(minuten % 60).padStart(2, '0')}`;
  }
  return `${(minuten / ARBEITSTAG_MIN).toFixed(1).replace('.', ',')} Arbeitstage`;
}

function Spalte({ spalte }) {
  const { gruppeName, offen, tag, aeltesteMin,
          inBearbeitung, wartendAufAndere, tickets } = spalte;

  return (
    <div className="wallboard-spalte">
      {/* title: auf der Raumanzeige zeigt niemand mit der Maus darauf, aber
          wenn jemand die Seite am Rechner oeffnet, steht der volle Name da. */}
      <div className="wallboard-gruppe" title={gruppeName}>{gruppeName}</div>

      <div className="wallboard-zahlen">
        {/* „Unbearbeitet", nicht „Offen": Gezaehlt wird nur, was bei uns liegt
            UND niemandem gehoert — die Tickets, für die jemand aufstehen muss.
            Was bereits in Bearbeitung ist, steht in der Fusszeile. */}
        <div className="wallboard-zahl">
          <span className={`wallboard-zahl-wert ${offen === 0 ? 'ist-ok' : ''}`}>{offen}</span>
          <span className="wallboard-zahl-label">Unbearbeitet</span>
        </div>
        {/* Die grosse Zahl ist der TAGESWERT: vom Eingang in diese Inbox bis
            zur Übernahme durch einen Agenten, für die heute eingegangenen
            Tickets. Nicht zu verwechseln mit „wie lange warten die gerade
            offenen schon" — das steht in der Fusszeile. */}
        <div className="wallboard-zahl">
          <span className={`wallboard-zahl-wert ${ampelKlasse(tag?.avgMin)}`}
            style={{ fontSize: 'clamp(20px, 2.1vw, 52px)' }}>
            {dauer(tag?.avgMin)}
          </span>
          <span className="wallboard-zahl-label">Ø bis Übernahme heute</span>
        </div>
      </div>

      <div className="wallboard-liste">
        {/* Zwei verschiedene Leer-Meldungen: „nichts da" und „nichts
            Dringendes, aber sehr wohl etwas zu tun" sind auf einer Wand
            grundverschiedene Aussagen. */}
        {tickets.length === 0 ? (
          <div className="wallboard-leer">
            {offen === 0
              ? 'Nichts Unbearbeitetes'
              : <>Keine dringenden Tickets<span className="wallboard-leer-sub">{offen} unbearbeitet mit Priorität Normal</span></>}
          </div>
        ) : tickets.map(t => (
          <div key={t.id} className={`wallboard-ticket wallboard-ticket--${t.prioKey}`}>
            <span className="wallboard-ticket-nr">#{t.id}</span>
            {/* Nur Nummer und Betreff — kein Kundenname. Was nicht in die
                Zeile passt, wird abgeschnitten. */}
            <span className="wallboard-ticket-text">
              {t.prioritaet && <span className="wallboard-ticket-prio">{t.prioritaet}</span>}
              {t.subject}
            </span>
            <span className={`wallboard-ticket-zeit ${ampelKlasse(t.wartetMin)}`}>
              {dauer(t.wartetMin)}
            </span>
          </div>
        ))}
      </div>

      {/* Kleingedrucktes, das die grossen Zahlen einordnet: der Median gegen
          den Schnitt (ein Altfall zieht den Schnitt weit hoch), wie viele
          heutige Eingänge noch niemand angenommen hat, und wie lange das
          älteste offene Ticket bereits liegt. */}
      {/* Bewusst knapp gehalten (Fachentscheid 21.09.2026): „noch nicht
          übernommen" und der Durchschnitt der Wartenden sind entfallen — die
          grosse Zahl oben sagt schon, wie viel unbearbeitet liegt, und das
          Älteste ist der Fall, auf den jemand reagieren muss. Beide Werte
          liefert die Route weiterhin, falls sie wieder gebraucht werden. */}
      <div className="wallboard-fuss">
        {/* Der Median ist am 29.09.2026 entfallen — er hatte aufgehoert zu
            messen, wofuer er gedacht war (siehe Begruendung in wallboard.js).
            An seine Stelle tritt eine Zahl, die man ohne Statistik-Kenntnisse
            lesen kann: wie viele Anrufe heute direkt angenommen wurden. */}
        {tag?.eingaengeHeute > 0 && <>
          {tag.eingaengeHeute} heute eingegangen
          {tag.direktAngenommen > 0 && <> · {tag.direktAngenommen} direkt angenommen</>} ·{' '}
        </>}
        Ältestes {dauer(aeltesteMin)}
        {inBearbeitung > 0 && <> · {inBearbeitung} in Bearbeitung</>}
        {wartendAufAndere > 0 && <> · {wartendAufAndere} warten auf Kunde/Dritte</>}
      </div>
    </div>
  );
}

export default function WallboardPage() {
  // Jede Minute nachsehen, ob wir (noch) in der Servicezeit sind — das ist der
  // Taktgeber fuer alles Weitere. Kostet nichts, loest aber sekundengenau am
  // Servicebeginn die erste Abfrage aus, statt bis zum naechsten 5-Minuten-
  // Takt zu warten.
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setJetzt(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const service = istServicezeit(jetzt);

  // top=5: mehr Zeilen passen auf 42" nicht mehr lesbar nebeneinander.
  // enabled=service: Ausserhalb der Servicezeit wird NICHT geladen; die zuletzt
  // geholten Daten bleiben stehen (useApi behaelt sie).
  const { data, error, refetch } = useApi('/api/wallboard', { top: 5 }, { poll: false, enabled: service });
  const [hoeheRef, hoeheStil] = useVolleHoehe();
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Takt an der UHR ausrichten, nicht am Startzeitpunkt der Seite
  // (Fachentscheid 21.09.2026): Alle Anzeigen holen zur selben vollen
  // Fuenf-Minuten-Marke, damit der Fernseher im Raum und die Reiter der
  // Agenten dieselben Zahlen zeigen. Vorher lief jede Anzeige auf ihrer
  // eigenen Uhr ab Seitenaufruf — zwei Bildschirme konnten bis zu fuenf
  // Minuten auseinanderliegen.
  //
  // Dass dadurch alle gleichzeitig anfragen, ist gewollt und im Backend
  // abgesichert: Eine laufende Berechnung wird geteilt statt vervielfacht
  // (siehe laufendeBerechnungen in routes/wallboard.js).
  useEffect(() => {
    if (!service) return undefined;
    let intervall;
    const bisZurMarke = NEULADEN_MS - (Date.now() % NEULADEN_MS);
    const start = setTimeout(() => {
      refetchRef.current();
      intervall = setInterval(() => refetchRef.current(), NEULADEN_MS);
    }, bisZurMarke);
    return () => { clearTimeout(start); clearInterval(intervall); };
  }, [service]);

  // Nach Mitternacht duerfen die Zahlen vom Vorabend nicht stehen bleiben —
  // „4 offen" von gestern 17:50 waere um 06:00 schlicht falsch, und der
  // Tageswert bezieht sich ohnehin auf einen Tag, der vorbei ist. Die Anzeige
  // zeigt dann bis zum Servicebeginn nur noch den Hinweis.
  const datenVeraltet = !!data && tagesSchluessel(new Date(data.stand)) !== tagesSchluessel(jetzt);

  // Erster Aufbau: keine Nullen zeigen — auf einer Wand wird jede Zahl
  // geglaubt, auch eine falsche. Der Aufbau dauert eine halbe Minute, weil das
  // Zendesk-Suchlimit die Abfragen streckt; danach kommt alles aus dem Cache.
  // Kein Inhalt zeigen: beim ersten Aufbau, und ausserhalb der Servicezeit,
  // wenn die letzten Zahlen von einem vergangenen Tag stammen.
  const leer = (!data && !error) || (!service && datenVeraltet);
  if (leer) {
    return <div className="wallboard" ref={hoeheRef} style={hoeheStil}>
      <div className="wallboard-kopf">
        <span className="wallboard-titel">Support-Wallboard</span>
        <span className="wallboard-stand">
          {service ? 'wird geladen…'
            : `ausserhalb der Servicezeiten · ab ${SERVICE_VON}:00 Uhr wieder`}
        </span>
      </div>
    </div>;
  }

  const stand = new Date(data.stand).toLocaleTimeString('de-CH');

  return (
    <div className="wallboard" ref={hoeheRef} style={hoeheStil}>
      <div className="wallboard-kopf">
        <span className="wallboard-titel">Support-Wallboard</span>
        {/* Der Zeitstempel sagt bewusst „berechnet um", nicht „Stand": Die
            Zahlen stammen aus gestaffelten Zwischenspeichern und sind etwas
            aelter als dieser Zeitpunkt — „Offen" wenige Minuten, der Tageswert
            unter Umstaenden laenger. „Stand" haette eine Genauigkeit
            versprochen, die es nicht gibt. */}
        <span className="wallboard-stand">
          {!service
            ? `ausserhalb der Servicezeiten · letzter Stand ${stand}`
            : error
              ? `keine Verbindung · letzter Stand ${stand}`
              : `berechnet um ${stand}`}
        </span>
      </div>

      <div className="wallboard-spalten">
        {(data?.spalten || []).map(s => <Spalte key={s.gruppeId} spalte={s} />)}
      </div>
    </div>
  );
}
